import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import CounterControl from '../CounterControl/CounterControl';
import CharacteristicInput from '../CharacteristicInput/CharacteristicInput';
import TextAreaControl from '../TextAreaControl/TextAreaControl';
import ConsequencesControl, { Consequence } from '../ConsequencesControl/ConsequencesControl';
import { CharacterConsequence, CharacterSheet } from '../../types/CharacterSheet';
import './styles.css';
import { Card } from '../Card';

interface FormProps {
    formData: CharacterSheet;
    onFormChange: (data: CharacterSheet) => void;
    onTotalDeathChange?: (active: boolean) => void;
    nameLabelAction?: React.ReactNode;
}

const BRIO_MIN = 0;
const STRESS_MAX = 10;
const SHADOW_MAX = 10;

function normalizeConsequences(value: unknown): CharacterConsequence[] {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is CharacterConsequence => {
        return Boolean(
            item &&
            typeof item === 'object' &&
            typeof item.id === 'string' &&
            typeof item.description === 'string' &&
            (item.type === 'leve' || item.type === 'moderada' || item.type === 'grave' || item.type === 'crítica')
        );
    });
}

function areConsequencesEqual(a: CharacterConsequence[], b: CharacterConsequence[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

const defaultFormData: CharacterSheet = {
    name: '',
    occupation: '',
    description: '',
    shadow: '0',
    agility: undefined,
    strength: undefined,
    dexterity: undefined,
    culture: undefined,
    craft: undefined,
    temperance: undefined,
    insight: undefined,
    brio: '3',
    stress: '0',
    notes: ''
};

const Form = forwardRef<{ reset: () => void }, FormProps>(
    ({ formData, onFormChange, onTotalDeathChange, nameLabelAction }, ref) => {
        const [consequences, setConsequences] = useState<Consequence[]>(() => {
            const fromForm = normalizeConsequences(formData.consequences);
            if (fromForm.length > 0) return fromForm;
            const stored = localStorage.getItem('consequences');
            if (!stored) return [];
            try {
                return normalizeConsequences(JSON.parse(stored));
            } catch {
                return [];
            }
        });

        useEffect(() => {
            localStorage.setItem('consequences', JSON.stringify(consequences));
        }, [consequences]);

        useEffect(() => {
            const fromForm = normalizeConsequences(formData.consequences);
            setConsequences(prev => (areConsequencesEqual(prev, fromForm) ? prev : fromForm));
        }, [formData.consequences]);

        useEffect(() => {
            const fromForm = normalizeConsequences(formData.consequences);
            if (areConsequencesEqual(fromForm, consequences)) return;
            onFormChange({
                ...formData,
                consequences,
            });
        }, [consequences, formData, onFormChange]);

        // Easter egg: detect "total death" state and notify parent
        useEffect(() => {
            const brio = parseInt(formData.brio ?? '0');
            const stress = parseInt(formData.stress ?? '0');
            const shadow = parseInt(formData.shadow ?? '0');
            const isTotalDeath = brio === BRIO_MIN && stress === STRESS_MAX && shadow === SHADOW_MAX;
            if (onTotalDeathChange) {
                onTotalDeathChange(isTotalDeath);
            }
        }, [formData.brio, formData.stress, formData.shadow, onTotalDeathChange]);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value } = e.target;
            onFormChange({
                ...formData,
                [name]: value === '' ? undefined : value,
            });
        };

        const handleCounterChange = (name: string, value: number) => {
            onFormChange({
                ...formData,
                [name]: value.toString(),
            });
        };

        // Expose reset method to parent via ref
        useImperativeHandle(ref, () => ({
            reset: () => {
                onFormChange({ ...defaultFormData });
                setConsequences([]);
            }
        }));

        return (
            <Card>
                <form className='wrapper character-sheet-form'>
                    <div className='section section-character'>
                        <h2>Personaje</h2>
                        <div>
                            <label htmlFor="name" className="form-name-label">
                                <span>Nombre</span>
                                {nameLabelAction ? <span>{nameLabelAction}</span> : null}
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name ?? ''}
                                onChange={handleChange}
                                id='name'
                            />
                        </div>
                        <div>
                            <label htmlFor='occupation'>Profesión</label>
                            <input
                                type="text"
                                name="occupation"
                                value={formData.occupation ?? ''}
                                onChange={handleChange}
                                id='occupation'
                                placeholder="Con qué se gana la vida"
                            />
                        </div>
                        <TextAreaControl
                            name="description"
                            label="Descripción"
                            value={formData.description ?? ''}
                            onChange={handleChange}
                            placeholder="Apariencia, motivaciones, etc."
                            rows={5}
                            cols={30}
                        />
                    </div>

                    <div className='section section-traits'>
                        <h2>Características</h2>
                        <p className='note'>Rellena con los valores 1, 1, 2, 2, 2, 3, 3</p>
                        <div className='form-characteristics'>
                            <CharacteristicInput
                                name="agility"
                                label="Agilidad:"
                                value={formData.agility}
                                min={1}
                                max={3}
                                onChange={handleChange}
                            />
                            <CharacteristicInput
                                name="strength"
                                label="Fuerza:"
                                value={formData.strength}
                                min={1}
                                max={3}
                                onChange={handleChange}
                            />
                            <CharacteristicInput
                                name="dexterity"
                                label="Destreza:"
                                value={formData.dexterity}
                                min={1}
                                max={3}
                                onChange={handleChange}
                            />
                            <CharacteristicInput
                                name="culture"
                                label="Cultura:"
                                value={formData.culture}
                                min={1}
                                max={3}
                                onChange={handleChange}
                            />
                            <CharacteristicInput
                                name="craft"
                                label="Oficio:"
                                value={formData.craft}
                                min={1}
                                max={3}
                                onChange={handleChange}
                            />
                            <CharacteristicInput
                                name="temperance"
                                label="Templanza:"
                                value={formData.temperance}
                                min={1}
                                max={3}
                                onChange={handleChange}
                            />
                            <CharacteristicInput
                                name="insight"
                                label="Perspicacia:"
                                value={formData.insight}
                                min={1}
                                max={3}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className='section section-status'>
                        <h2>Estado</h2>
                        <div className='counter-wrapper'>
                            <CounterControl
                                name="brio"
                                label="Brío"
                                value={parseInt(String(formData.brio ?? defaultFormData.brio))}
                                min={BRIO_MIN}
                                max={3}
                                onChange={handleCounterChange}
                            />
                            <CounterControl
                                name="stress"
                                label="Estrés"
                                value={parseInt(String(formData.stress ?? defaultFormData.stress))}
                                min={0}
                                max={STRESS_MAX}
                                onChange={handleCounterChange}
                                showThermometer
                            />
                            <CounterControl
                                name="shadow"
                                label="Sombra"
                                value={parseInt(String(formData.shadow ?? defaultFormData.shadow))}
                                min={0}
                                max={SHADOW_MAX}
                                onChange={handleCounterChange}
                                showThermometer
                            />
                        </div>
                        <ConsequencesControl
                            value={consequences}
                            onChange={setConsequences}
                        />
                    </div>
                    <div className='section section-notes'>
                        <h2 id="notes-heading">Notas</h2>
                        <TextAreaControl
                            name="notes"
                            label="Notas"
                            value={formData.notes ?? ''}
                            onChange={handleChange}
                            rows={5}
                            cols={30}
                            placeholder="Notas adicionales"
                            hideLabel
                            ariaLabelledBy="notes-heading"
                        />
                    </div>
                </form>
            </Card>
        );
    }
);

export default Form;
